import { useSettings } from '../SettingsProvider';
import { useWMSSettings } from '../../WMSLayers/useWMSSettings';
import { getOsmUrlHeadersFromSettings } from '../../utils/getOsmUrlHeadersFromSettings';
import { useEffect, useState } from 'react';
import { getS57Url } from '../../utils/getS57Url';

export interface BaseLayerConfig {
  id: string;
  type: 'osm' | 'wms' | 's57' | 'blue';
  title: string;
  description: string;
  url: string;
  headers?: Record<string, string>;
  center?: [number, number]; // Used only for map snapshots
  zoom?: number; // Used only for map snapshots
}

export const useBaseLayerConfigs = () => {
  const { settings } = useSettings();
  const wmsSettings = useWMSSettings();
  const { url: osmUrl, headers: osmHeaders } =
    getOsmUrlHeadersFromSettings(settings);

  const [baseLayerConfigs, setBaseLayerConfigs] = useState<BaseLayerConfig[]>([
    {
      id: 'osm',
      type: 'osm',
      title: 'OpenStreetMap',
      description: 'A high-quality map view.',
      url: osmUrl,
      headers: osmHeaders,
    },
    {
      id: 'carto-dark',
      type: 'osm',
      title: 'Dark (CartoDB)',
      description: 'A dark map view, ideal for night mode.',
      url: 'https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
    },
    {
      id: 's57',
      type: 's57',
      title: 'S-57',
      description: 'A sea map view.',
      url: getS57Url(),
    },
    {
      id: 'blue',
      type: 'blue',
      title: 'Blue',
      description: 'A solid blue base map for offline use.',
      url: '',
    },
  ]);

  useEffect(() => {
    const baseWmsLayers: BaseLayerConfig[] = wmsSettings.wmsInfos.flatMap(
      (wmsLayer) =>
        wmsLayer.layers
          ?.filter((layer) => layer.isBase)
          .map((layer) => ({
            id: layer.id,
            type: 'wms',
            title: layer.name,
            description: `Layer from ${wmsLayer.name}`,
            url: wmsLayer.url,
          })) || [],
    );

    setBaseLayerConfigs((prevConfigs: BaseLayerConfig[]) => {
      const existingIds = new Set(prevConfigs.map((config) => config.id));
      const newBaseWmsLayers = baseWmsLayers.filter(
        (layer) => !existingIds.has(layer.id),
      );
      return [...prevConfigs, ...newBaseWmsLayers];
    });
  }, [wmsSettings.wmsInfos]);

  return { baseLayerConfigs, setBaseLayerConfigs };
};
